import type { RoiId } from '~/domain/changes/ids';
import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { Environment } from '~/engine/data/Environment';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { StructureOption } from './OperationFormControls';

export interface OperationShellRenderProps {
    environment: Environment;
    inputRoi: RegionOfInterest;
    structures: StructureOption[];
    onInputROIChange: (roiId: RoiId) => void;
}

interface OperationContentShellProps {
    children: (props: OperationShellRenderProps) => React.ReactNode;
}

/**
 * Outer guard shared by all SDF-operation panels.
 *
 * Reads the environment and selected ROI from the edit-target resolver (the
 * active tile's Environment in grid mode), resolves the input ROI, computes
 * the flat structures list, and renders a "please select structure" fallback
 * when no valid ROI is available. When everything is valid the render-prop
 * children are invoked with the resolved values.
 */
export default function OperationContentShell({
    children,
}: OperationContentShellProps) {
    const { environment, roiId: selectedROI } = useActiveEditTarget();
    const [inputROI, setInputROI] = useState(selectedROI);

    // Track the latest selectedROI so the input resets when the user
    // changes the active structure from outside the panel.
    useEffect(() => {
        setInputROI(selectedROI);
    }, [selectedROI]);

    if (!environment?.structureSet || selectedROI === null) {
        return (
            <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="pleaseSelectStructure" />
            </Typography>
        );
    }

    const structures: StructureOption[] =
        environment.structureSet.structures.map((r) => ({
            name: r.name,
            roiId: r.roiId,
            color: {
                R: Math.round(r.color[0] * 255),
                G: Math.round(r.color[1] * 255),
                B: Math.round(r.color[2] * 255),
            },
            isWriteProtected: !r.canModifyContours,
        }));

    const inputRoi =
        (inputROI === null
            ? undefined
            : environment.structureSet.getRoi(inputROI)) ??
        environment.structureSet.getRoi(selectedROI);

    if (!inputRoi) {
        return (
            <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="pleaseSelectStructure" />
            </Typography>
        );
    }

    return (
        <>
            {children({
                environment,
                inputRoi,
                structures,
                onInputROIChange: setInputROI,
            })}
        </>
    );
}
